import { readFragment } from '~publish/graphql'
import type { FragmentOf } from '~publish/graphql'
import { isOfType } from '~publish/helpers/typeGuards'
import { extractUploadIdFromVideoUrl } from '~publish/legacy/composer/composer/utils/DataImportUtils'
import type {
  RpcUpdateDocument,
  RpcUpdateImage,
  RpcUpdateMedia,
  RpcUpdateVideo,
} from '~publish/legacy/post/types'

import { PostMediaAsset_Asset } from '../PostCard/PostMedia/PostMediaAsset'
import type { PostMediaAsset_AssetData } from '../PostCard/PostMedia/PostMediaAsset'

/**
 * The media half of a channel-less draft's composer prefill. The composer's
 * import pipeline (DataImportUtils.formatMetaData) reads `media` + `extra_media`
 * to seed the omni draft's images/video/document, the same path that already
 * restores the draft text.
 *
 * All assets are carried in their original source order.
 */
export type PrefillMedia = {
  media?: RpcUpdateMedia
  extra_media?: RpcUpdateMedia[]
}

const toRpcImage = (asset: PostMediaAsset_AssetData): RpcUpdateImage | null => {
  if (!isOfType(asset, 'ImageAsset')) return null
  const { source, thumbnail, image } = asset
  return {
    progress: '100',
    uploaded: source,
    thumbnail,
    source: undefined,
    alt_text: image.altText || null,
    width: String(image.width),
    height: String(image.height),
    picture: source,
    photo: source,
  }
}

const toRpcVideo = (asset: PostMediaAsset_AssetData): RpcUpdateVideo | null => {
  if (!isOfType(asset, 'VideoAsset')) return null
  const { source, thumbnail, video } = asset
  return {
    progress: '100',
    uploaded: source,
    thumbnail,
    uploading_type: 'video',
    source,
    video: {
      id: extractUploadIdFromVideoUrl(source) ?? '',
      title: '',
      thumbnails: thumbnail ? [thumbnail] : [],
      thumb_offset: 0,
      details: {
        duration: 0,
        duration_millis: 0,
        file_size: 0,
        width: video.width,
        height: video.height,
        transcoded_location: source,
        location: source,
      },
    },
  }
}

// The source URL's last path segment, used as a document title when the API
// doesn't carry one on the card fragment.
const fileNameFromUrl = (url: string): string => {
  try {
    const path = new URL(url).pathname
    return decodeURIComponent(path.split('/').pop() || '') || 'Document'
  } catch {
    return 'Document'
  }
}

const toRpcDocument = (
  asset: PostMediaAsset_AssetData,
): RpcUpdateDocument | null => {
  if (!isOfType(asset, 'DocumentAsset')) return null
  const { source, thumbnail, document } = asset
  return {
    progress: '100',
    uploaded: source,
    thumbnail,
    uploading_type: 'document',
    document: {
      id: '',
      title: fileNameFromUrl(source),
      details: {
        location: source,
        pageCount: document.numPages,
        fileSizeBytes: document.filesize ?? 0,
      },
    },
  }
}

export function mapDraftContentToPrefillMedia(
  assets: FragmentOf<typeof PostMediaAsset_Asset>[],
): PrefillMedia {
  const read = readFragment(PostMediaAsset_Asset, assets)

  const ordered: RpcUpdateMedia[] = read.reduce<RpcUpdateMedia[]>(
    (acc, asset) => {
      const media =
        toRpcImage(asset) ?? toRpcVideo(asset) ?? toRpcDocument(asset)
      if (media) acc.push(media)
      return acc
    },
    [],
  )

  if (ordered.length === 0) {
    return {}
  }

  return { media: ordered[0], extra_media: ordered.slice(1) }
}
